import type { ReportRow } from '@/store/slices/reportingV2/reportingV2.api';
import { Typography } from '@mui/material';
import { clientMoneyCoverageNote } from '../format';
import { COLORS } from '../theme';

interface IProps {
  rows: ReportRow[];
  /** Suppressed entirely when the viewer's role can't see Client Spend at all — a
   * coverage ratio would otherwise leak whether line items are priced to someone
   * who isn't permitted to see the prices themselves. */
  restricted?: boolean;
}

/**
 * Visible, in-the-open disclosure that a Client Spend/Client CPM/Margin
 * aggregate is built from a mix of priced and unpriced rows (flank#4448) — one
 * shared component so every widget that aggregates these metrics discloses
 * coverage the same way, rather than each re-deriving its own wording.
 *
 * Deliberately a plain block of text, not a `Tooltip`: the whole point is that
 * nobody should have to hover to learn a total is only partially real.
 * Renders nothing once every row in view has a sold CPM — a fully-covered
 * view has nothing to disclose.
 */
const ClientMoneyCoverageNote = ({ rows, restricted }: IProps) => {
  if (restricted) return null;
  const note = clientMoneyCoverageNote(rows);
  if (!note) return null;
  return (
    <Typography variant='caption' sx={{ color: COLORS.muted, display: 'block', pt: 1 }}>
      {note}
    </Typography>
  );
};

export default ClientMoneyCoverageNote;
